import { startTransition, useOptimistic, useState, useTransition } from 'react';

import {
  mapOrganizationMembershipHistoryItemToRow,
  MembershipHistoryCard
} from '@treneo/features/admin';
import type { IOrganizationUserDto } from '@treneo/models';
import { PermissionCodes } from '@treneo/shared/constants';
import { useDateTimeConfig } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { useAppParams } from '@treneo/shared/router';
import {
  AdminPageWrapper,
  Box,
  Card,
  CardContent,
  ConfirmationDialog,
  DataGridWrapper,
  FormControlLabel,
  Switch,
  toast,
  useConfirmationDialog,
  useQueryErrorHandling,
  useServerPagination
} from '@treneo/shared/ui';

import { InviteTrainerInlineInput, RequireOrganizationPermission } from '../../components';
import {
  useCancelOrganizationInvitationMutation,
  useInviteTrainerMutation,
  useOrganizationInvitationsQuery,
  useOrganizationMembershipHistoryQuery,
  useOrganizationUsersQuery,
  useRemoveUserFromOrganizationMutation,
  useRevokeTrainerRoleMutation
} from '../../data';
import { TRAINER_INVITATION_STATUS } from '../../data/trainer-invitation-status.constants';
import { createOrganizationUsersColumns, filterUsersByTrainerStatus } from '../../utils';
import { handleMutationError } from '../../utils/error-handling.utils';

const OrganizationUsersPageContent = () => {
  const { t } = useAppTranslations();
  const { dateFormat, timeFormat } = useDateTimeConfig();
  const { organizationId } = useAppParams<{ organizationId: string }>();
  const {
    openConfirm,
    closeConfirm,
    isOpen: isConfirmOpen,
    content: confirmContent,
    handleConfirm: handleConfirmYes
  } = useConfirmationDialog();
  const inviteTrainerMutation = useInviteTrainerMutation();
  const removeUserMutation = useRemoveUserFromOrganizationMutation();
  const revokeTrainerRoleMutation = useRevokeTrainerRoleMutation();
  const cancelInvitationMutation = useCancelOrganizationInvitationMutation();
  const [showMembershipHistory, setShowMembershipHistory] = useState(false);
  const [isPendingHistory, startHistoryTransition] = useTransition();
  const usersPagination = useServerPagination();
  const membershipHistoryPagination = useServerPagination();
  const {
    data: usersData,
    isLoading,
    error,
    refetch
  } = useOrganizationUsersQuery(organizationId ?? '', {
    request: { page: usersPagination.page, pageSize: usersPagination.pageSize },
    enabled: !!organizationId
  });
  const { data: invitationsData } = useOrganizationInvitationsQuery(organizationId ?? '', {
    enabled: !!organizationId
  });
  const { data: membershipHistoryData, isLoading: isLoadingMembershipHistory } =
    useOrganizationMembershipHistoryQuery(organizationId ?? '', {
      request: {
        page: membershipHistoryPagination.page,
        pageSize: membershipHistoryPagination.pageSize
      },
      enabled: !!organizationId && showMembershipHistory
    });
  const [showOnlyTrainers, setShowOnlyTrainers] = useState(false);

  const invitations = invitationsData?.invitations ?? [];
  const pendingInvitations = invitations.filter(
    inv => inv.status === TRAINER_INVITATION_STATUS.PENDING
  );
  const pendingInvitationByEmail = (() => {
    const map = new Map<string, { id: string }>();
    for (const inv of pendingInvitations) {
      const key = inv.email?.trim().toLowerCase() ?? '';
      if (key) {
        map.set(key, { id: inv.id });
      }
    }
    return map;
  })();

  const users = usersData?.users ?? [];
  const usersTotalCount = usersData?.totalCount ?? 0;
  const [optimisticUsers, addOptimisticUser] = useOptimistic(
    users,
    (
      state: IOrganizationUserDto[],
      action: { type: 'invite' | 'revoke'; user: IOrganizationUserDto }
    ) => {
      switch (action.type) {
        case 'invite':
          return state.map(u => (u.id === action.user.id ? { ...u, isTrainer: true } : u));
        case 'revoke':
          return state.map(u => (u.id === action.user.id ? { ...u, isTrainer: false } : u));
        default:
          return state;
      }
    }
  );

  const handleInviteUser = async (user: IOrganizationUserDto) => {
    if (!organizationId) {
      return;
    }

    startTransition(() => {
      addOptimisticUser({ type: 'invite', user });
    });

    try {
      await inviteTrainerMutation.mutateAsync({
        organizationId,
        email: user.email,
        origin: window.location.origin
      });
      toast.success(t('trainers.inviteSuccess'));
    } catch (error: unknown) {
      handleMutationError(error, 'trainers.inviteError', t);
      void refetch();
    }
  };

  const executeRemoveUser = async (user: IOrganizationUserDto) => {
    if (!organizationId) {
      return;
    }

    try {
      await removeUserMutation.mutateAsync({ organizationId, userId: user.userId });
      toast.success(t('trainers.removeFromOrganizationSuccess'));
      void refetch();
    } catch (error: unknown) {
      handleMutationError(error, 'organizations.errors.removeUserFromOrganizationFailed', t);
      void refetch();
    }
  };

  const handleRemoveUser = (user: IOrganizationUserDto) => {
    openConfirm({
      content: t('trainers.confirmRemoveTrainer'),
      onConfirm: () => {
        void executeRemoveUser(user);
      }
    });
  };

  const executeRevokeTrainerRole = async (user: IOrganizationUserDto) => {
    if (!organizationId) {
      return;
    }

    startTransition(() => {
      addOptimisticUser({ type: 'revoke', user });
    });

    try {
      await revokeTrainerRoleMutation.mutateAsync({
        organizationId,
        userId: user.userId
      });
      toast.success(t('trainers.revokeTrainerRoleSuccess'));
      void refetch();
    } catch (error: unknown) {
      handleMutationError(error, 'organizations.errors.revokeTrainerRoleFailed', t);
      void refetch();
    }
  };

  const handleRevokeTrainerRole = (user: IOrganizationUserDto) => {
    openConfirm({
      content: t('trainers.confirmRevokeTrainerRole'),
      onConfirm: () => {
        void executeRevokeTrainerRole(user);
      }
    });
  };

  const handleCancelInvitation = (invitationId: string) => {
    if (!organizationId) {
      return;
    }

    cancelInvitationMutation.mutate(
      { organizationId, invitationId },
      {
        onSuccess: () => {
          toast.success(t('trainers.invitationCancelled'));
        },
        onError: (err: unknown) => {
          handleMutationError(err, 'organizations.errors.cancelOrganizationInvitationFailed', t);
        }
      }
    );
  };

  const filteredUsers = filterUsersByTrainerStatus(optimisticUsers, showOnlyTrainers);
  const membershipHistoryRows = (membershipHistoryData?.history ?? []).map(
    mapOrganizationMembershipHistoryItemToRow
  );
  const membershipHistoryTotalCount = membershipHistoryData?.totalCount ?? 0;

  const handleLoadMembershipHistory = (): void => {
    startHistoryTransition(() => {
      setShowMembershipHistory(prev => !prev);
    });
  };

  const columns = createOrganizationUsersColumns({
    t,
    dateFormat,
    timeFormat,
    onInviteUser: (user: IOrganizationUserDto) => {
      void handleInviteUser(user);
    },
    onRemoveUser: handleRemoveUser,
    onRevokeTrainerRole: handleRevokeTrainerRole,
    onCancelInvitation: handleCancelInvitation,
    pendingInvitationByEmail,
    isInviting: inviteTrainerMutation.isPending,
    isRemoving: removeUserMutation.isPending,
    isRevoking: revokeTrainerRoleMutation.isPending,
    isCancelling: cancelInvitationMutation.isPending
  });

  const { errorComponent } = useQueryErrorHandling({
    error: organizationId ? error : undefined,
    refetch: () => {
      void refetch();
    }
  });

  if (errorComponent) {
    return errorComponent;
  }

  return (
    <AdminPageWrapper
      title={t('admin.organizationsMenu.users')}
      count={usersTotalCount}
      countLabel={t('admin.usersCount')}
      useCard={false}
    >
      <InviteTrainerInlineInput
        organizationId={organizationId ?? ''}
        users={optimisticUsers}
        onInviteSuccess={() => {
          void refetch();
        }}
      />

      <Card>
        <CardContent>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={showOnlyTrainers}
                  onChange={e => setShowOnlyTrainers(e.target.checked)}
                />
              }
              label={t('trainers.showOnlyTrainers')}
            />
          </Box>

          <DataGridWrapper
            rows={filteredUsers}
            columns={columns}
            loading={isLoading}
            {...usersPagination.gridPaginationProps(usersTotalCount)}
            getRowId={(row: IOrganizationUserDto) => row.id}
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <MembershipHistoryCard
          history={membershipHistoryRows}
          isLoading={isLoadingMembershipHistory}
          showHistory={showMembershipHistory}
          onToggleHistory={handleLoadMembershipHistory}
          showUserColumn={true}
          titleKey='admin.organizationMembershipHistory'
          isPending={isPendingHistory}
          totalCount={membershipHistoryTotalCount}
          page={membershipHistoryPagination.page}
          pageSize={membershipHistoryPagination.pageSize}
          onPaginationChange={membershipHistoryPagination.onPaginationChange}
        />
      </Box>

      <ConfirmationDialog
        open={isConfirmOpen}
        content={confirmContent}
        onNo={closeConfirm}
        onYes={handleConfirmYes}
      />
    </AdminPageWrapper>
  );
};

export const OrganizationUsersPage = () => {
  return (
    <RequireOrganizationPermission permissionCode={PermissionCodes.ManageOrganization}>
      <OrganizationUsersPageContent />
    </RequireOrganizationPermission>
  );
};
